export function isPortalSelfEmbedUrl(url: string): boolean {
  const trimmed = url.trim();

  if (!trimmed) {
    return true;
  }

  try {
    const resolved = new URL(trimmed, window.location.origin);
    return resolved.origin === window.location.origin;
  } catch {
    return false;
  }
}

export interface ResolveEmbeddedNavigationUrlInput {
  applicationBaseUrl: string;
  navigationTarget?: string | null;
}

export interface ResolveEmbeddedNavigationUrlResult {
  url: string;
  didFallback: boolean;
  error?: 'invalidApplicationBaseUrl' | 'invalidNavigationTarget';
  applicationBaseUrl: string;
  navigationTarget?: string;
}

function tryParseHttpUrl(value: string): URL | null {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

function getApplicationBaseDirectory(applicationUrl: URL): URL {
  const baseDirectory = new URL(applicationUrl.toString());

  if (baseDirectory.pathname.endsWith('/')) {
    return baseDirectory;
  }

  const finalPathSegment = baseDirectory.pathname.split('/').at(-1) ?? '';
  baseDirectory.pathname = finalPathSegment.includes('.')
    ? baseDirectory.pathname.slice(
        0,
        baseDirectory.pathname.lastIndexOf('/') + 1,
      )
    : `${baseDirectory.pathname}/`;

  return baseDirectory;
}

function fallbackResolution(
  error: 'invalidApplicationBaseUrl' | 'invalidNavigationTarget',
  applicationBaseUrl: string,
  navigationTarget?: string,
): ResolveEmbeddedNavigationUrlResult {
  return {
    url: applicationBaseUrl || navigationTarget || '',
    didFallback: true,
    error,
    applicationBaseUrl,
    navigationTarget,
  };
}

export function resolveEmbeddedNavigationUrl({
  applicationBaseUrl,
  navigationTarget,
}: ResolveEmbeddedNavigationUrlInput): ResolveEmbeddedNavigationUrlResult {
  const normalizedApplicationBaseUrl = applicationBaseUrl.trim();
  const normalizedNavigationTarget = navigationTarget?.trim();

  if (!normalizedNavigationTarget) {
    return {
      url: normalizedApplicationBaseUrl,
      didFallback: false,
      applicationBaseUrl: normalizedApplicationBaseUrl,
      navigationTarget: normalizedNavigationTarget,
    };
  }

  if (/^[a-z][a-z\d+.-]*:/i.test(normalizedNavigationTarget)) {
    const absoluteNavigationTarget = tryParseHttpUrl(
      normalizedNavigationTarget,
    );

    if (!absoluteNavigationTarget) {
      return fallbackResolution(
        'invalidNavigationTarget',
        normalizedApplicationBaseUrl,
        normalizedNavigationTarget,
      );
    }

    return {
      url: absoluteNavigationTarget.toString(),
      didFallback: false,
      applicationBaseUrl: normalizedApplicationBaseUrl,
      navigationTarget: normalizedNavigationTarget,
    };
  }

  if (!normalizedApplicationBaseUrl) {
    return fallbackResolution(
      'invalidApplicationBaseUrl',
      normalizedApplicationBaseUrl,
      normalizedNavigationTarget,
    );
  }

  const parsedApplicationUrl = tryParseHttpUrl(normalizedApplicationBaseUrl);
  if (!parsedApplicationUrl) {
    return fallbackResolution(
      'invalidApplicationBaseUrl',
      normalizedApplicationBaseUrl,
      normalizedNavigationTarget,
    );
  }

  try {
    const applicationBaseDirectory =
      getApplicationBaseDirectory(parsedApplicationUrl);
    const resolvedNavigationUrl = new URL(
      normalizedNavigationTarget,
      applicationBaseDirectory,
    );

    if (!normalizedNavigationTarget.includes('?')) {
      resolvedNavigationUrl.search = parsedApplicationUrl.search;
    }

    if (!normalizedNavigationTarget.includes('#')) {
      resolvedNavigationUrl.hash = parsedApplicationUrl.hash;
    }

    return {
      url: resolvedNavigationUrl.toString(),
      didFallback: false,
      applicationBaseUrl: normalizedApplicationBaseUrl,
      navigationTarget: normalizedNavigationTarget,
    };
  } catch {
    return fallbackResolution(
      'invalidNavigationTarget',
      normalizedApplicationBaseUrl,
      normalizedNavigationTarget,
    );
  }
}
